import React, { lazy, Suspense, Component, useMemo, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import {
  Box,
  CssBaseline,
  Container,
  CircularProgress,
  ThemeProvider,
  createTheme
} from '@mui/material';
import { ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { Link } from 'react-router-dom';
import { LibraryBooks as LibraryBooksIcon } from '@mui/icons-material';
// Components
import NavBar from './components/NavBar';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DarkModeProvider, useDarkMode } from './contexts/DarkModeContext';
import NoticeGuard from './components/NoticeGuard';

// --- LAZY LOAD PAGES --- 
const Home = lazy(() => import('./pages/Home')); // Original home with Get Started & Quick Access
const Login = lazy(() => import('./pages/Login'));
const Notice = lazy(() => import('./pages/Notice')); // New notice page
const ForgotPassword = lazy(() => import('./pages/ForgotPassword')); // Forgot password page
const Knowledge = lazy(() => import('./pages/Knowledge'));
const Register = lazy(() => import('./pages/Register')); 
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const OnboardingAcademy = lazy(() => import('./pages/OnboardingAcademy'));
const AcademyAdmin = lazy(() => import('./pages/AcademyAdmin'));
const DocumentAccess = lazy(() => import('./pages/DocumentAccess'));
const Suggestions = lazy(() => import('./pages/Suggestions'));
const Rewards = lazy(() => import('./pages/Rewards'));
const SharedDocuments = lazy(() => import('./pages/SharedDocuments'));


// --- THEME DEFINITION --- 
// MARK brand palette: red (#e21f4e), white (#fefefe), black.
const createAppTheme = (mode) => createTheme({
  palette: {
    mode,
    primary: {
      main: '#e21f4e', // MARK red
      light: '#ff4d78',
      dark: '#b8163a',
      contrastText: '#fefefe',
    },
    secondary: {
      main: mode === 'dark' ? '#fefefe' : '#000000', // White / black
      light: mode === 'dark' ? '#ffffff' : '#262626',
      dark: mode === 'dark' ? '#d4d4d4' : '#000000',
      contrastText: mode === 'dark' ? '#000000' : '#fefefe',
    },
    background: {
      default: mode === 'dark' ? '#211C1A' : '#F3EEE7',
      paper: mode === 'dark' ? '#3F3833' : '#FBF8F4',
    },
    info: {
      main: '#404040', // Neutral dark gray
    },
    success: {
      main: '#1a8f4c', // Muted green, kept for clear positive feedback
    },
    warning: {
      main: '#e2a71f', // Muted amber
    },
    error: {
      main: '#d81b3f', // Deep red, distinct from primary brand red
    },
    divider: mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
    text: {
      primary: mode === 'dark' ? '#F9F5F0' : '#18140F',
      secondary: mode === 'dark' ? '#DACFC3' : '#544B41',
    }
  },
  typography: {
    fontFamily: '"Poppins", system-ui, sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
    body1: {
      fontFamily: '"Poppins", system-ui, sans-serif',
    },
    body2: {
      fontFamily: '"Poppins", system-ui, sans-serif',
    },
    subtitle1: {
      fontFamily: '"Poppins", system-ui, sans-serif',
    },
    subtitle2: {
      fontFamily: '"Poppins", system-ui, sans-serif',
    },
    caption: {
      fontFamily: '"Poppins", system-ui, sans-serif',
    },
    overline: {
      fontFamily: '"Poppins", system-ui, sans-serif',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0,0,0,0.05)',
    '0px 4px 8px rgba(0,0,0,0.08)',
    '0px 6px 12px rgba(0,0,0,0.1)',
    '0px 8px 16px rgba(0,0,0,0.12)',
    // Rest of the shadows array
    ...Array(20).fill('0px 10px 20px rgba(0,0,0,0.15)'),
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 16px',
          boxShadow: '0px 2px 4px rgba(0,0,0,0.05)',
        },
        contained: {
          boxShadow: '0px 4px 8px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0px 4px 10px rgba(0,0,0,0.05)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 4px 10px rgba(0,0,0,0.05)',
        },
      },
    },
  },
});

// --- UTILITY COMPONENTS --- 

// Simple Error Boundary component
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error: error };
  }

  componentDidCatch(error, errorInfo) {
    // You can also log the error to an error reporting service
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return <h1>Something went wrong rendering this section. Error: {this.state.error?.message || 'Unknown error'}</h1>;
    }

    return this.props.children;
  }
}

// Protected route component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, isLoading } = useAuth();
  const isAuthenticated = !!user;

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>;
  }

  if (!isAuthenticated) {
    // Flags are cleared by AuthContext on SIGNED_OUT or by main App useEffect on token check
    return <Navigate to="/login" replace />;
  }

  const userRoles = user?.roles || [];
  const hasRequiredRole = allowedRoles.every(role => userRoles.includes(role));

  if (!hasRequiredRole) {
    return <Navigate to="/home" replace />;
  }

  return children; // Render children directly, no disclaimer overlay here
};

// Admin route component
const AdminRoute = ({ children }) => {
  const { user, isLoading } = useAuth();
  const [renderCount, setRenderCount] = React.useState(0); // Add render count state

  React.useEffect(() => { // Increment render count on mount/update
      setRenderCount(prev => prev + 1);
  }, [user, isLoading]);


  // ---- Add AdminRoute Debug Log ----
  
  // ---- End AdminRoute Debug Log ----

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>;
  }

  const isAuthenticated = !!user;
  const isAdmin = user?.email === 'admin@markenzoworldwide.com';

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/home" replace />;
  }

  // Wrap children in Error Boundary
  return (
    <ErrorBoundary>
      {children}
    </ErrorBoundary>
  );
};

// Loading Indicator Component (Example)
const LoadingIndicator = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
    <CircularProgress />
  </Box>
);

// --- CORE APP STRUCTURE --- 

const MarkGround = () => {
  const { pathname } = useLocation();
  const home = pathname === '/' || pathname === '/home';

  useEffect(() => {
    document.body.classList.toggle('mark-page', !home);
    return () => document.body.classList.remove('mark-page');
  }, [home]);

  return (
    <>
      <div className="mark-ground" aria-hidden="true" />
      <div className="mark-wash" aria-hidden="true">
        <i className="w1" />
        <i className="w2" />
        <i className="w3" />
        <i className="grain" />
      </div>
    </>
  );
};

// Main app content with theme provider
const AppContent = () => {
  const { isDarkMode } = useDarkMode();
  const { user } = useAuth(); // Get user to pass to NavBar if needed
  const theme = useMemo(() => createAppTheme(isDarkMode ? 'dark' : 'light'), [isDarkMode]);
  

  useEffect(() => {
    // Check for auth token in localStorage on initial load
    const token = localStorage.getItem('authToken');
    if (!token && user) {
      // If no token in localStorage but user context exists (e.g. from previous session memory)
      // it might be stale, so clear auth flags to prompt login if necessary.
      // This handles cases where localStorage might be cleared but context persists temporarily.
      // Consider if this logic is appropriate for your auth flow, especially with remember-me features.
      // clearAuthFlags(); // Example: uncomment if you want to force re-auth if localStorage token is missing
    }
  }, [user]); // Add clearAuthFlags to dependency array

  

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>}>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'transparent' }}>
            <MarkGround />
            {user && <NavBar />} 
            <Box component="main" className="mark-main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/notice" element={<ProtectedRoute><Notice /></ProtectedRoute>} />
                
                <Route path="/" element={<ProtectedRoute><NoticeGuard><Home /></NoticeGuard></ProtectedRoute>} />
                <Route path="/home" element={<ProtectedRoute><NoticeGuard><Home /></NoticeGuard></ProtectedRoute>} />
                <Route path="/chat" element={<Navigate to="/home" replace />} />
                <Route path="/chat/:chatId" element={<Navigate to="/home" replace />} />
                <Route path="/knowledge" element={<ProtectedRoute><NoticeGuard><Knowledge /></NoticeGuard></ProtectedRoute>} />
                {/* Admin check lives inside the page, matching how /knowledge gates its admin tabs */}
                <Route path="/document-access" element={<ProtectedRoute><NoticeGuard><DocumentAccess /></NoticeGuard></ProtectedRoute>} />
                <Route path="/shared-documents" element={<ProtectedRoute><NoticeGuard><SharedDocuments /></NoticeGuard></ProtectedRoute>} />
                <Route path="/onboarding" element={<ProtectedRoute><NoticeGuard><OnboardingPage /></NoticeGuard></ProtectedRoute>} />
                <Route path="/academy" element={<ProtectedRoute><NoticeGuard><OnboardingAcademy /></NoticeGuard></ProtectedRoute>} />
                <Route path="/academy-admin" element={<ProtectedRoute><NoticeGuard><AcademyAdmin /></NoticeGuard></ProtectedRoute>} />
                <Route path="/suggestions" element={<ProtectedRoute><NoticeGuard><Suggestions /></NoticeGuard></ProtectedRoute>} />
                <Route path="/rewards" element={<ProtectedRoute><NoticeGuard><Rewards /></NoticeGuard></ProtectedRoute>} />
                {/* Weekly Report hidden — redirect old URLs home */}
                <Route path="/report" element={<Navigate to="/home" replace />} />
                <Route path="/admin-report" element={<Navigate to="/home" replace />} />
                

                {/* Fallback route - redirect to home or login based on auth */}
                <Route path="*" element={<Navigate to={user ? "/home" : "/login"} replace />} />
              </Routes>
            </Box>
          </Box>
        </Suspense>
      </Router>
    </ThemeProvider>
  );
};

// Wrap app with providers and Suspense
const App = () => {
  useEffect(() => {
    // Check for Supabase token. The key might be different, e.g., 'sb-auth-token' or specific to your Supabase setup.
    // Inspect localStorage in your browser's developer tools to find the correct key used by Supabase.
    const supabaseTokenKey = Object.keys(localStorage).find(key => key.startsWith('sb-') && key.includes('-auth-token'));
    const token = supabaseTokenKey ? localStorage.getItem(supabaseTokenKey) : null;
    
    if (!token) {
      localStorage.removeItem('pendingDisclaimer');
      localStorage.removeItem('disclaimerAcknowledgedThisLogin');
    }
  }, []);

  return (
    <ErrorBoundary>
      <DarkModeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </DarkModeProvider>
    </ErrorBoundary>
  );
};

export default App;
